import { supabase } from "../supabase/SupabaseClient";
import { actual } from "./quien";

import { filtroDePersonas, MOTIVOS_CIERRE } from "../shared/herramientas";
import {
  correccionDeEntrega, itemsDeDevolucionParaLaBase, itemsParaLaBase,
} from "../shared/entregasPanol";
import { traerTodas } from "./paginar";

export * from "../shared/herramientas";
export * from "../shared/entregasPanol";

/* ¿El error es porque la base todavía no tiene esa columna? Las de asignar o
   prestar (`vence_el`, `cierre_motivo`) llegan con una migración que puede
   aplicarse después del deploy: sin ellas la pantalla tiene que seguir
   andando, no caerse entera. */
export function faltaColumna(error, columna) {
  const m = String(error?.message || error || "");
  return m.includes(columna) &&
    /column|schema cache|does not exist|Could not find|42703|PGRST204/i.test(m);
}

/* ¿La base todavía tiene la `entregar_del_panol` de nueve parámetros? */
export const faltaFuncionNueva = (error) =>
  /Could not find the function|PGRST202|function .*entregar_del_panol.* does not exist/i
    .test(String(error?.message || error || ""));

const AVISO_SIN_FECHA = "Quedó prestada, pero sin fecha de devolución: falta una " +
  "actualización de la base, así que no van a salir los recordatorios por WhatsApp.";

/* El tablero de herramientas, contra la base.
 *
 * ── «NO PUDE LEER» NO ES «NO HAY NINGUNA» ────────────────────────────────────
 *
 * Todas las lecturas devuelven `{filas, error}` y nunca una lista vacía a
 * secas cuando fallan. Un tablero vacío por una consulta caída se ve
 * exactamente igual que un taller sin herramientas, y la diferencia es que en
 * el primer caso las herramientas están prestadas y nadie se entera. Misma
 * regla que `services/crt`.
 *
 * ── EL ESTADO SALE DE LA VISTA ───────────────────────────────────────────────
 *
 * Se lee `taller_tablero`, que ya trae DISPONIBLE / PRESTADA / DEMORADA /
 * MANTENIMIENTO resuelto y, si está prestada, quién la tiene y desde cuándo.
 * Una sola consulta pinta la pantalla, y la definición de «demorada» es la
 * misma para el Hub y para el bot.
 */

/* ── Leer ─────────────────────────────────────────────────────────────────── */

export async function tablero() {
  const { data, error } = await supabase
    .from("taller_tablero")
    .select("*")
    .order("y", { ascending: true })
    .order("x", { ascending: true });
  if (error) {
    console.warn("[herramientas] no pude leer el tablero:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: data || [], error: "" };
}

/* El historial de una herramienta: las últimas entregas, cerradas y abierta. */
export async function historialDe(herramientaId, tope = 20) {
  const { data, error } = await supabase
    .from("taller_prestamo")
    .select("*")
    .eq("herramienta_id", herramientaId)
    .is("anulado_en", null)
    .order("retirado_en", { ascending: false })
    .limit(tope);
  if (error) {
    console.warn("[herramientas] no pude leer el historial:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: data || [], error: "" };
}

/* Qué tiene una persona sin devolver. Es la otra mitad del buscador: se busca
 * a alguien y se le reclama todo junto. */
export async function loQueTiene(dni) {
  const { data, error } = await supabase
    .from("taller_prestamo")
    .select("*, herramienta:taller_herramienta(nombre, codigo, tipo)")
    .eq("persona_dni", dni)
    .is("devuelto_en", null)
    .is("anulado_en", null)
    .order("retirado_en", { ascending: false });
  if (error) {
    console.warn("[herramientas] no pude leer lo de la persona:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: data || [], error: "" };
}

/* TODO lo que se le prestó a una persona: lo que tiene y lo que devolvió.
 *
 * Igna, 15/09: «debería haber una tarjeta que diga cuándo se le prestó algo. Y
 * lo quiero histórico. Cuando lo devuelva que quede registro de cuándo lo
 * devolvió y cuánto tiempo lo tuvo».
 *
 * NO HAY QUE GUARDAR NADA NUEVO: `taller_prestamo` ya escribe `retirado_en`,
 * `devuelto_en` y quién hizo cada cosa desde el primer día. El historial
 * existía y no se miraba desde ningún lado — `loQueTiene` mira sólo lo abierto,
 * porque su pregunta es qué reclamar.
 *
 * LAS ANULADAS NO ENTRAN. Un préstamo anulado es uno que se cargó por error:
 * «esto no pasó». Mostrarlo en un historial lo convierte en algo que sí pasó y
 * se deshizo, que es otra cosa.
 *
 * Se pide una de más que el tope para saber si quedó algo afuera. Una lista
 * cortada se ve igual que una completa, y acá lo que faltaría son préstamos.
 */
/* Todas las entregas abiertas, de todos: las DEFINITIVAS arman las cajas
   (`agruparCajas`) y las otras son lo que el pañol tiene afuera
   (`cuentaPanol`). Una sola consulta y se agrupa en el cliente: son decenas
   de filas, no miles, y así la pantalla no depende de una vista más. */
const COLUMNAS_ABIERTAS = "id, herramienta_id, persona_dni, persona_nombre, retirado_en, " +
  "definitivo, devuelto_en, anulado_en, herramienta:taller_herramienta(dias_prestamo)";

export async function entregasAbiertas() {
  const pedir = (columnas) => supabase
    .from("taller_prestamo")
    .select(columnas)
    .is("devuelto_en", null)
    .is("anulado_en", null);
  let { data, error } = await pedir(`${COLUMNAS_ABIERTAS}, vence_el`);
  // Sin la migración de asignar o prestar, se lee como antes.
  if (error && faltaColumna(error, "vence_el")) ({ data, error } = await pedir(COLUMNAS_ABIERTAS));
  if (error) {
    console.warn("[herramientas] no pude leer las cajas:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: data || [], error: "" };
}

/* La empresa de cada persona con caja, para pintar la caja de su color
   (`aspectoDeCaja`). `personas.empresa` guarda el CUIT. Si no se puede leer,
   las cajas salen galvanizadas: el color es adorno, no un dato que se pierde. */
export async function empresasDe(dnis) {
  const lista = [...new Set((dnis || []).filter(Boolean).map(String))];
  if (!lista.length) return { porDni: {}, puestoPorDni: {}, error: "" };
  const { data, error } = await supabase
    .from("personas")
    .select("dni, empresa, puesto")
    .in("dni", lista);
  if (error) {
    console.warn("[herramientas] no pude leer las empresas:", error.message);
    return { porDni: {}, puestoPorDni: {}, error: error.message };
  }
  /* P-311: de paso, el puesto — si es CHOFER, su caja es de elementos. */
  const porDni = {};
  const puestoPorDni = {};
  for (const f of data || []) {
    porDni[String(f.dni)] = f.empresa;
    puestoPorDni[String(f.dni)] = f.puesto || null;
  }
  return { porDni, puestoPorDni, error: "" };
}

export async function prestamosDe(dni, tope = 20) {
  const { data, error } = await supabase
    .from("taller_prestamo")
    .select("*, herramienta:taller_herramienta(nombre, codigo, tipo)")
    .eq("persona_dni", dni)
    .is("anulado_en", null)
    .order("retirado_en", { ascending: false })
    .limit(tope + 1);
  if (error) {
    console.warn("[herramientas] no pude leer los préstamos:", error.message);
    return { filas: [], hayMas: false, error: error.message };
  }
  const filas = data || [];
  return { filas: filas.slice(0, tope), hayMas: filas.length > tope, error: "" };
}

/* ── Quién se la lleva ────────────────────────────────────────────────────────
 *
 * LA PERSONA SALE DEL PADRÓN Y NO DE UN CAMPO DE TEXTO. Es el agujero que
 * tenía el sistema viejo: «Pérez» escrito a mano no se validaba contra nadie,
 * dos Pérez eran el mismo y «Perez» sin tilde era otro distinto. Acá se elige
 * de una lista y lo que se guarda es el DNI.
 *
 * Se ofrece a los ACTIVOS del taller primero, pero no SÓLO a ellos: un chofer
 * también se lleva herramientas, y una lista que no lo tenga obliga a anotarlo
 * en otro lado — que es como se dejan de usar los sistemas.
 */
/* LAS COLUMNAS QUE `personas` TIENE DE VERDAD. Se pedía `sector`, que la
   tabla nunca tuvo: PostgREST contestaba «column personas.sector does not
   exist» y la búsqueda de personas —prestar en el pañol, armar una caja— no
   traía a nadie (Igna, 02/10, buscando «insa»). El «sector» que muestran las
   pantallas sale del área o, si no hay, de la sucursal. */
export const COLUMNAS_PERSONA = "dni, apellido, nombres, area, sucursal, legajo, puesto";

/* DE QUÉ PARTE DE LA EMPRESA ES (ticket 327, Nico 06/10): «quise armar una
   caja nueva y me aparecen 3 personas con el mismo apellido. Sería útil saber
   de qué parte de la empresa son (taller, chofer, administrativo)».

   El padrón lo tiene en `puesto` —CHOFER, TALLER, ADMINISTRATIVO, TRAFICO…— y
   esa columna ya se pedía; lo que no se hacía era mostrarla. El «sector»
   salía del área, que nadie tiene cargada, o de la sucursal: con tres
   homónimos de la misma sucursal, los tres renglones eran iguales salvo por
   el DNI, que no le dice nada al que busca.

   Va PRIMERO porque es lo que distingue a una persona de otra; la sucursal
   queda después. Se guarda en mayúsculas y se muestra como palabra. */
export const parteDe = (p) => {
  const puesto = String(p?.puesto || "").trim();
  if (!puesto) return "";
  return puesto.toLowerCase().replace(/(^|\s)\p{L}/gu, (l) => l.toUpperCase());
};

export const conSector = (p) => ({
  ...p,
  sector: [parteDe(p), p.area || p.sucursal].filter(Boolean).join(" · ") || null,
});

/* `todos` (Nico, 13/10, corrigiendo el papel de Bell Julio): por defecto se
   busca entre los ACTIVOS, que es a quienes se les presta. Pero un papel
   viejo puede ser de alguien que ya se fue, o de alguien que está en el
   padrón con la ficha a medio cargar (sin estado): con el filtro puesto no
   aparecía, el papel quedaba sin persona y la caja no se armaba nunca. Con
   `todos` se busca en el padrón entero y cada fila dice si figura activa. */
export async function personas(busqueda = "", { todos = false } = {}) {
  let q = supabase
    .from("personas")
    .select(todos ? `${COLUMNAS_PERSONA}, estado` : COLUMNAS_PERSONA);
  if (!todos) q = q.eq("estado", "true");
  q = q
    .order("apellido", { ascending: true })
    .limit(50);
  // `filtroDePersonas`: la coma de «apellido, nombre» rompía el filtro.
  const f = filtroDePersonas(busqueda);
  if (f) q = q.or(f);
  const { data, error } = await q;
  if (error) {
    console.warn("[herramientas] no pude leer el padrón:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: (data || []).map(conSector), error: "" };
}

/* Sólo sirve con lo que trae `personas(…, { todos: true })`. */
export const figuraActivo = (p) => p?.estado === undefined || String(p.estado) === "true";

export const nombreDe = (p) =>
  [p?.apellido, p?.nombres].filter(Boolean).join(", ") || String(p?.dni || "");

/* ── Escribir ─────────────────────────────────────────────────────────────── */

/* Prestar. El nombre se CONGELA en la fila: el histórico tiene que seguir
 * diciendo quién se la llevó aunque después le corrijan el apellido en el
 * padrón o le den la baja.
 *
 * Si la herramienta ya estaba prestada, la base lo rechaza —el índice parcial
 * `taller_prestamo_una_abierta`— y acá se traduce a una frase que se entiende.
 * Es a propósito que lo impida la base y no esta función: dos personas
 * apurando el botón al mismo tiempo pasan por acá dos veces. */
export async function prestar({ herramientaId, herramientaIds, persona,
                                nota = "", unidad = "", definitivo = false,
                                venceEl = null }) {
  const ids = (herramientaIds && herramientaIds.length)
    ? herramientaIds
    : (herramientaId ? [herramientaId] : []);
  if (!ids.length || !persona?.dni) {
    return { ok: false, error: "falta la herramienta o la persona", hechas: [] };
  }
  const yo = actual();
  const comun = {
    persona_dni: persona.dni,
    persona_nombre: nombreDe(persona),
    quien: yo?.mail || null,
    quien_nombre: yo?.nombre || null,
    nota: nota || null,
    /* #262 (Nico): a qué unidad (tractor/furgón) va la entrega, si aplica. El
       pañol reparte repuestos y herramientas para una unidad, no siempre para
       una persona a secas. Opcional; se guarda el interno tal cual, trim. */
    unidad: (unidad || "").trim() || null,
    /* #272 (Nico): «la mayoría no las presto, se las doy y son de ellos». Una
       entrega definitiva queda para la persona: no es una deuda a devolver, así
       que no se ofrece «devolver» ni se arrastra como préstamo abierto a
       reclamar. */
    definitivo: !!definitivo,
  };
  /* Prestar con fecha (07/10): el día en que la tiene que devolver. Lo
     asignado no lleva fecha (la base además lo impide). */
  if (!definitivo && venceEl) comun.vence_el = venceEl;
  let aviso = "";

  /* UNA POR UNA Y NO TODAS EN UN INSERT.
   *
   * Con las cinco en una sola llamada, que una ya esté prestada tira la fila
   * entera y no se anota NINGUNA — y el que apretó el botón se queda pensando
   * que no se prestó nada cuando en realidad cuatro estaban libres. Una por
   * una, se anotan las que se pueden y se devuelve la lista de las que no,
   * con su motivo. Es la misma decisión que el bot toma del lado de WhatsApp.
   */
  const hechas = [];
  const fallidas = [];
  for (const id of ids) {
    let { error } = await supabase
      .from("taller_prestamo")
      .insert([{ ...comun, herramienta_id: id }]);
    if (error && comun.vence_el && faltaColumna(error, "vence_el")) {
      // La base todavía no tiene la fecha: se presta igual, y se dice.
      delete comun.vence_el;
      aviso = AVISO_SIN_FECHA;
      ({ error } = await supabase
        .from("taller_prestamo")
        .insert([{ ...comun, herramienta_id: id }]));
    }
    if (!error) { hechas.push(id); continue; }
    /* El tope ya no es un índice único sino el trigger `taller_prestamo_cabe`,
       que cuenta los préstamos abiertos contra la cantidad. Se reconocen los
       dos: una pestaña abierta desde antes del 16/09 puede seguir viendo el
       mensaje viejo, y el de Postgres crudo no le dice nada a nadie. */
    const sinStock = /no quedan unidades/i.test(error.message);
    const yaEsta = /taller_prestamo_una_abierta|duplicate key/i.test(error.message);
    fallidas.push({ id, error: sinStock
      ? "no quedan unidades libres — alguien se llevó la última"
      : yaEsta
        ? "ya figuraba prestada — alguien la registró primero"
        : error.message });
  }
  if (!hechas.length) {
    return { ok: false, hechas, fallidas,
             error: fallidas[0]?.error || "no pude anotar la entrega" };
  }
  return { ok: true, error: "", hechas, fallidas, aviso };
}

/* Devolver. Se cierra POR ID de préstamo y no por herramienta: si entre que se
 * abrió la pantalla y se apretó el botón alguien registró otra cosa, cerrar
 * «el préstamo abierto de la amoladora» cerraría el que no era. */
/* `motivo`: por qué se cierra (`MOTIVOS_CIERRE`, en shared/herramientas). */
export async function devolver({ prestamoId, nota = "", motivo = "devuelta" }) {
  if (!prestamoId) return { ok: false, error: "falta el préstamo" };
  const yo = actual();
  const parche = {
    devuelto_en: new Date().toISOString(),
    devuelto_quien: yo?.mail || null,
    devuelto_quien_nombre: yo?.nombre || null,
  };
  /* La nota de la devolución se SUMA a la de la entrega: la observación que
     se escribió al darla (27/10) no se pierde al cerrarla. */
  if (nota) {
    const { data: antes } = await supabase
      .from("taller_prestamo").select("nota").eq("id", prestamoId);
    const previa = String((Array.isArray(antes) ? antes[0]?.nota : "") || "").trim();
    parche.nota = [previa, nota].filter(Boolean).join(" · ");
  }
  if (MOTIVOS_CIERRE[motivo]) parche.cierre_motivo = motivo;
  const cerrar = (p) => supabase
    .from("taller_prestamo")
    .update(p)
    .eq("id", prestamoId)
    .is("devuelto_en", null)
    .select("id");
  let { data, error } = await cerrar(parche);
  if (error && faltaColumna(error, "cierre_motivo")) {
    // Sin la migración se cierra igual; el motivo se pierde, el cierre no.
    const sinMotivo = { ...parche };
    delete sinMotivo.cierre_motivo;
    ({ data, error } = await cerrar(sinMotivo));
  }
  if (error) return { ok: false, error: error.message };
  if (!data || !data.length) {
    return { ok: false,
             error: "Ese préstamo ya estaba cerrado. Actualizá la pantalla." };
  }
  return { ok: true, error: "" };
}

/* ── El padrón de herramientas (administración) ───────────────────────────── */

/* La lista COMPLETA, incluidas las dadas de baja. La pantalla del tablero lee
 * la vista, que filtra por `activo`; acá hace falta ver también lo apagado,
 * porque si no, dar de baja una herramienta es hacerla desaparecer sin forma
 * de volver. */
export async function padron() {
  const { data, error } = await supabase
    .from("taller_herramienta")
    .select("*")
    .order("nombre", { ascending: true });
  if (error) {
    console.warn("[herramientas] no pude leer el padrón:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: data || [], error: "" };
}

/* `imagen_url` y `silueta_url` NO están: desde el 14/09 la herramienta se
   dibuja y no se fotografía, y dejarlas acá haría que una ficha vieja abierta
   en una pestaña las siguiera escribiendo. Las columnas quedan en la tabla,
   anotadas como muertas. (`imagen_url` revivió un día, del 08/10 al 09/10,
   con #633: una sola foto pegada a la fila. Las fotos de P-348 son varias y
   van en `taller_herramienta_foto`; ver `services/herramientasFotos.js`.)
   `dias_prestamo` TAMPOCO (06/10, Nico: la ficha ya no lo pide). Si se
   mandara, el alta escribiría lo que traiga el objeto —o un null que pisa el
   `default 7` de la columna y choca con el `not null`—; sin la clave, la nueva
   nace con el default y la edición deja el plazo que la fila ya tenía. */
const CAMPOS = ["codigo", "nombre", "tipo", "categoria", "sector", "panol",
                "x", "y", "ancho", "alto", "rotacion",
                "cantidad",
                "fuera_de_servicio", "fuera_motivo",
                "activo", "nota"];

const soloCampos = (h) =>
  Object.fromEntries(Object.entries(h).filter(([k]) => CAMPOS.includes(k)));

function conAutor(fila) {
  const yo = actual();
  return yo ? { ...fila, usuario: yo.mail, usuario_nombre: yo.nombre } : fila;
}

/* Alta y edición pasan por la misma puerta: los dos escriben la misma fila y
 * separar en dos funciones duplicaría la validación y el sellado del autor. */
export async function guardarHerramienta(h) {
  if (!String(h?.codigo || "").trim() || !String(h?.nombre || "").trim()) {
    return { ok: false, error: "El código y el nombre son obligatorios." };
  }
  /* LA CANTIDAD SE VALIDA ACÁ Y EN LA BASE. El `check (cantidad > 0)` es el que
     manda —una pestaña vieja no lo puede esquivar—, pero un 0 tipeado tiene que
     decir por qué no se guarda antes de volver como un error de Postgres. */
  if (h.cantidad !== undefined && h.cantidad !== null && h.cantidad !== "") {
    const c = Number(h.cantidad);
    if (!Number.isInteger(c) || c < 1) {
      return { ok: false, error: "La cantidad tiene que ser 1 o más." };
    }
  }
  const fila = conAutor(soloCampos(h));
  const q = h.id
    ? supabase.from("taller_herramienta")
        .update({ ...fila, actualizado_en: new Date().toISOString() })
        .eq("id", h.id)
    : supabase.from("taller_herramienta").insert([fila]);
  const { data, error } = await q.select("id");
  if (error) {
    const repe = /codigo_unico|duplicate key/i.test(error.message);
    return { ok: false, error: repe
      ? `Ya hay una herramienta con el código ${h.codigo}.`
      : error.message };
  }
  /* El id vuelve porque el ALTA lo necesita: las fotos que se eligieron antes
     de guardar (P-348) se cuelgan de él. */
  return { ok: true, error: "", id: data?.[0]?.id ?? h.id ?? null };
}

/* DAR DE BAJA NO ES BORRAR, y no hay un botón de borrar.
 *
 * Los préstamos apuntan a la herramienta: borrarla se llevaría puesto el
 * historial de quién la tuvo, que es justamente lo que este sistema vino a
 * guardar. `activo = false` la saca del tablero y deja el pasado en pie. */
export async function darDeBaja(id, baja = true) {
  const { error } = await supabase
    .from("taller_herramienta")
    .update({ activo: !baja, actualizado_en: new Date().toISOString() })
    .eq("id", id);
  return error ? { ok: false, error: error.message } : { ok: true, error: "" };
}

/* ── Las fotos ────────────────────────────────────────────────────────────── */

/* LAS FOTOS VIVEN EN `services/herramientasFotos.js`, y son OPTATIVAS.
 *
 * Hasta el 14/09 había UNA foto por herramienta, pegada a la fila
 * (`imagen_url`), obligatoria y en lugar del dibujo. Igna la sacó: *«no les
 * vamos a pedir foto, es carente de sentido; un taladro es un taladro»*. Las
 * herramientas se dibujan (`shared/dibujosHerramienta.js`) y eso no cambió.
 *
 * El 09/10 (P-348, Nico: *«quiero agregar fotos de herramientas cuando agrego
 * una herramienta»*) volvieron de otra forma: VARIAS por herramienta, en su
 * propia tabla (`taller_herramienta_foto`), nunca pedidas y sin tocar el
 * tablero. `imagen_url` y `silueta_url` siguen muertas: esta función no las
 * escribe (ver `CAMPOS`).
 */

/* ── Acomodar el tablero ──────────────────────────────────────────────────── */

/* Se guardan TODAS las que se movieron de una y no una por una mientras se
 * arrastra: acomodar un tablero son veinte movimientos seguidos, y escribir en
 * cada uno llenaría la base de estados intermedios que nadie quiso guardar
 * —además de dejar el tablero a medio acomodar si alguien cierra la pestaña en
 * el medio—. Se arrastra en la pantalla, se aprieta Guardar, y recién ahí baja. */
export async function guardarPosiciones(posiciones) {
  const lista = Object.entries(posiciones || {});
  if (!lista.length) return { ok: true, error: "", guardadas: 0 };
  for (const [id, p] of lista) {
    const { error } = await supabase
      .from("taller_herramienta")
      .update({ x: p.x, y: p.y, ancho: p.ancho, alto: p.alto,
                actualizado_en: new Date().toISOString() })
      .eq("id", Number(id));
    if (error) return { ok: false, error: error.message, guardadas: 0 };
  }
  return { ok: true, error: "", guardadas: lista.length };
}

/* ── Las entregas del pañol y su papel (P-311) ────────────────────────────────
 *
 * Nico, 05/10: *«Estaría bueno que al asignar nuevas herramientas ya haga el
 * papel solo, se imprima, se firma y se almacena»*. Una entrega es el papel:
 * `panol_entregas`. Ver `shared/entregasPanol.js` y la migración
 * `20261005170000_herramientas_y_elementos_una_sola_entrega.sql`.
 */

export const BUCKET_CONSTANCIAS = "constancias";

const CAMPOS_ENTREGA = "id, persona_dni, persona_nombre, persona_legajo, destinatario, " +
  "panol, interno, items, entregado_en, observaciones, entregador, origen, revisar, " +
  "creado_en, creado_por, foto_path, foto_subida_en, foto_subida_por, anulado_en";

/* Todas las vigentes: arman las cajas (lo escrito a mano y qué papel falta)
   y la lista de actas sin persona. Son decenas, no miles. */
/* LO DEVUELTO NO ARMA CAJAS (tickets 328/329). `devuelto_en` llega con la
   migración `20261007223655_devolucion_de_herramientas.sql`, que se aplica
   aparte: sin ella se lee como antes, sin el filtro. */
const sinDevolver = (q) => q.is("devuelto_en", null);
const faltaDevuelto = (error) => faltaColumna(error, "devuelto_en");

export async function entregasDelPanol() {
  /* Hoy son 65; paginadas igual, para que el día que pasen de mil no se
     corten calladas (`sinLimitesFalsos.test.js`). */
  try {
    const pedir = (filtro) => traerTodas(() => filtro(supabase
      .from("panol_entregas")
      .select(CAMPOS_ENTREGA, { count: "exact" })
      .is("anulado_en", null))
      .order("creado_en", { ascending: false })
      .order("id", { ascending: false }));
    let filas;
    try {
      filas = await pedir(sinDevolver);
    } catch (error) {
      if (!faltaDevuelto(error)) throw error;
      filas = await pedir((q) => q);
    }
    return { filas, error: "" };
  } catch (error) {
    console.warn("[herramientas] no pude leer las entregas:", error?.message);
    return { filas: [], error: error?.message || "error" };
  }
}

export async function entregasDe(dni) {
  const pedir = (filtro) => filtro(supabase
    .from("panol_entregas")
    .select(CAMPOS_ENTREGA)
    .eq("persona_dni", dni)
    .is("anulado_en", null))
    .order("creado_en", { ascending: false });
  let { data, error } = await pedir(sinDevolver);
  if (error && faltaDevuelto(error)) ({ data, error } = await pedir((q) => q));
  if (error) {
    console.warn("[herramientas] no pude leer los papeles:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: data || [], error: "" };
}

/* Anotar que devolvió TODO. Una sola llamada: o se anota todo —se cierran sus
   préstamos, sus papeles dejan de armar la caja y queda la devolución con
   quién la recibió— o no se anota nada. */
export async function devolverTodo({ dni, items, observaciones = "" }) {
  if (!dni) return { ok: false, error: "falta de quién es", id: null };
  if (!items || !items.length) return { ok: false, error: "no hay nada para devolver", id: null };
  try {
    const { data, error } = await supabase.rpc("panol_devolver_todo", {
      p_persona_dni: String(dni),
      p_items: itemsDeDevolucionParaLaBase(items),
      p_observaciones: String(observaciones || "").trim() || null,
      p_motivo: "baja",
    });
    if (error) {
      console.warn("[herramientas] no pude anotar la devolución:", error.message);
      return { ok: false, error: motivoDevolucion(error.message), id: null };
    }
    return { ok: true, error: "", id: data ?? null };
  } catch (e) {
    return { ok: false, error: motivoDevolucion(e?.message || e), id: null };
  }
}

export function motivoDevolucion(mensaje) {
  const m = String(mensaje || "");
  if (/Could not find the function|PGRST202|panol_devolver_todo/i.test(m)) {
    return "Todavía no se puede anotar una devolución desde acá. No se anotó nada.";
  }
  if (/ya no figura en su caja/i.test(m)) {
    return "Alguien movió una de sus herramientas mientras tanto. Recargá y probá de nuevo: no se anotó nada.";
  }
  if (/cargar\.taller|42501/i.test(m)) return "No podés pasar.";
  return "No se pudo consultar.";
}

/* Todas las veces que devolvió todo, la más nueva primero (ticket 477): la
   última se muestra en la caja vacía y las anteriores, plegadas. Si la tabla
   no está o falla, una lista vacía: la caja se sigue viendo. */
export async function devolucionesDe(dni) {
  try {
    const { data, error } = await supabase
      .from("panol_devoluciones")
      .select("id, devuelto_en, recibio_nombre, faltantes, perdidas, items, observaciones")
      .eq("persona_dni", dni)
      .order("devuelto_en", { ascending: false })
      .limit(20);
    if (error) return [];
    return data || [];
  } catch (e) {
    return [];
  }
}

/* La última vez que devolvió todo. */
export async function ultimaDevolucion(dni) {
  return (await devolucionesDe(dni))[0] || null;
}

export async function entrega(id) {
  const { data, error } = await supabase
    .from("panol_entregas")
    .select(CAMPOS_ENTREGA)
    .eq("id", id)
    .maybeSingle();
  if (error) return { fila: null, error: error.message };
  return { fila: data || null, error: "" };
}

/* Los errores de la base, en castellano de mostrador. */
export function motivoEntrega(mensaje) {
  const m = String(mensaje || "");
  /* No dice «stock»: esa palabra mandó a Nico a cargar Stock en el depósito,
     que no cambia lo que se puede entregar (20/10). Lo que lo cambia es
     «Cuántas hay» de la herramienta. */
  if (/no quedan unidades/i.test(m)) {
    return "No quedan para entregar de una de las herramientas: alguien se llevó las últimas. Si llegaron más, subí «Cuántas hay» en Administrar. No se anotó nada.";
  }
  if (/es del pañol/i.test(m)) return `${m}. No se anotó nada.`;
  if (/row-level security|permission denied|42501/i.test(m)) {
    return "No tenés permiso para entregar del pañol.";
  }
  return m || "No pude anotar la entrega.";
}

/* Entregar: el papel y las herramientas, TODO O NADA (`entregar_del_panol`).
 * Si no alcanza el stock de una, no se anota ninguna — el papel tiene que
 * decir exactamente lo que salió. Devuelve `{ok, id, error}`. */
export async function entregar({ persona, destinatario, panol, renglones,
                                 interno = "", observaciones = "",
                                 definitivo = true, venceEl = null }) {
  const items = itemsParaLaBase(renglones);
  if (!persona || (!persona.dni && !persona.apellido)) {
    return { ok: false, id: null, error: "Falta a quién se le entrega." };
  }
  if (!items.length) return { ok: false, id: null, error: "La entrega no tiene nada." };
  const yo = actual();
  const base = {
    p_persona_dni: persona.dni ? String(persona.dni) : null,
    p_persona_nombre: nombreDe(persona),
    p_destinatario: destinatario === "chofer" ? "chofer" : "mecanico",
    p_panol: panol || null,
    p_items: items,
    p_interno: String(interno || "").trim() || null,
    p_observaciones: String(observaciones || "").trim() || null,
    p_persona_legajo: persona.legajo ? String(persona.legajo) : null,
    p_quien_nombre: yo?.nombre || null,
  };
  /* ASIGNAR va con los nueve parámetros de siempre: anda con la base vieja y
     con la nueva. PRESTAR suma `p_definitivo` y `p_vence_el`; si la base
     todavía no los conoce, se entrega asignado y se pasa a préstamo a mano
     (sin fecha), y se dice. */
  if (definitivo) {
    const { data, error } = await supabase.rpc("entregar_del_panol", base);
    if (error) return { ok: false, id: null, error: motivoEntrega(error.message) };
    return { ok: true, id: data, error: "" };
  }
  const { data, error } = await supabase.rpc("entregar_del_panol", {
    ...base, p_definitivo: false, p_vence_el: venceEl || null,
  });
  if (!error) return { ok: true, id: data, error: "" };
  if (!faltaFuncionNueva(error)) return { ok: false, id: null, error: motivoEntrega(error.message) };
  const viejo = await supabase.rpc("entregar_del_panol", base);
  if (viejo.error) return { ok: false, id: null, error: motivoEntrega(viejo.error.message) };
  const { error: errPrestar } = await supabase
    .from("taller_prestamo")
    .update({ definitivo: false })
    .eq("entrega_id", viejo.data);
  return { ok: true, id: viejo.data, error: "",
           aviso: errPrestar
             ? "Se entregó, pero quedó como asignada: no pude pasarla a préstamo."
             : AVISO_SIN_FECHA };
}

/* El papel de LO QUE YA TIENE, sin sacar nada del pañol: para las cajas que
 * se armaron antes de que hubiera papel, o por WhatsApp. Es una entrega con
 * `origen = relevamiento` y ningún préstamo nuevo. */
export async function papelDeLoQueTiene({ persona, destinatario, items }) {
  if (!persona?.dni || !(items || []).length) {
    return { ok: false, id: null, error: "No hay nada para poner en el papel." };
  }
  const yo = actual();
  const { data, error } = await supabase
    .from("panol_entregas")
    .insert([{
      persona_dni: String(persona.dni),
      persona_nombre: nombreDe(persona),
      persona_legajo: persona.legajo ? String(persona.legajo) : null,
      destinatario: destinatario === "chofer" ? "chofer" : "mecanico",
      items,
      origen: "relevamiento",
      entregador: yo?.nombre || null,
      observaciones: "Lo que tenía a la fecha (relevamiento de la caja).",
    }])
    .select("id")
    .single();
  if (error) return { ok: false, id: null, error: motivoEntrega(error.message) };
  return { ok: true, id: data?.id, error: "" };
}

/* La foto (o el PDF) del papel firmado. Se guarda la RUTA, nunca una URL: una
 * URL firmada se vence. Quién y cuándo la subió lo sella la base. */
export async function subirPapelFirmado(entregaFila, archivo) {
  if (!entregaFila?.id || !archivo) return { ok: false, error: "Falta la foto." };
  const ext = (String(archivo.name || "").split(".").pop() || "jpg")
    .toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const ruta = `entregas/${entregaFila.id}/${Date.now()}.${ext}`;
  const { error: errSubida } = await supabase.storage
    .from(BUCKET_CONSTANCIAS)
    .upload(ruta, archivo, { contentType: archivo.type || "image/jpeg" });
  if (errSubida) return { ok: false, error: errSubida.message };
  const { data, error } = await supabase
    .from("panol_entregas")
    .update({ foto_path: ruta })
    .eq("id", entregaFila.id)
    .select("id, foto_path, foto_subida_en, foto_subida_por");
  if (error) return { ok: false, error: error.message };
  if (!data || !data.length) {
    return { ok: false, error: "La foto subió pero no quedó asociada: no tenés permiso para completar la entrega." };
  }
  return { ok: true, error: "", fila: data[0] };
}

export async function linkPapel(ruta, segundos = 3600) {
  if (!ruta) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET_CONSTANCIAS)
    .createSignedUrl(ruta, segundos);
  return error ? null : data?.signedUrl || null;
}

/* Un acta vieja sin DNI (35 de las 64 de papel): elegir del padrón de quién
 * es. Se elige, no se adivina — misma regla que la ficha del chofer. */
export async function asignarPersona(entregaId, persona) {
  if (!entregaId || !persona?.dni) return { ok: false, error: "Falta la persona." };
  const { data, error } = await supabase
    .from("panol_entregas")
    .update({
      persona_dni: String(persona.dni),
      persona_nombre: nombreDe(persona),
      persona_legajo: persona.legajo ? String(persona.legajo) : null,
      revisar: false,
    })
    .eq("id", entregaId)
    .select("id");
  if (error) return { ok: false, error: error.message };
  if (!data || !data.length) return { ok: false, error: "No tenés permiso para completar la entrega." };
  return { ok: true, error: "" };
}

/* Corregir a mano lo que el papel dice: de quién es (nombre y DNI, Nico
 * 08/10), fecha, interno y la lista (Nico, 07/10). Ver `correccionDeEntrega`. Deja de estar «para revisar»: alguien lo
 * miró. */
export async function corregirEntrega(entregaId, datos) {
  if (!entregaId) return { ok: false, error: "Falta el papel." };
  const c = correccionDeEntrega(datos);
  if (c.error) return { ok: false, error: c.error };
  /* Con el DNI escrito a mano, el legajo sale del padrón si esa persona está
     (también la que ya se fue); si no está, no se arrastra el de otro. */
  if ("persona_dni" in c.cambios) {
    let legajo = null;
    if (c.cambios.persona_dni) {
      const { data: p } = await supabase
        .from("personas").select("legajo").eq("dni", c.cambios.persona_dni).limit(1);
      legajo = p?.[0]?.legajo ? String(p[0].legajo) : null;
    }
    c.cambios.persona_legajo = legajo;
  }
  const { data, error } = await supabase
    .from("panol_entregas")
    .update({ ...c.cambios, revisar: false })
    .eq("id", entregaId)
    .is("anulado_en", null)
    .select("id");
  if (error) return { ok: false, error: error.message };
  if (!data || !data.length) return { ok: false, error: "No tenés permiso para corregir la entrega." };
  return { ok: true, error: "" };
}

/* ── Quitar lo que se cargó de más ────────────────────────────────────────────
 *
 * Nico, 06/10: *«le cargué más de una vez a la misma caja sin querer y quiero
 * poder quitarla»*. Ver `papelesRepetidos` y `planQuitarCaja` en
 * `shared/entregasPanol.js`. Nada se borra: se ANULA (el papel) o se DEVUELVE
 * (lo asignado sin papel), y la base sella quién y cuándo.
 */

/* Anula los préstamos abiertos de un papel: «esto no salió», el stock vuelve. */
async function anularPrestamosDe(entregaId, yo) {
  const { data, error } = await supabase
    .from("taller_prestamo")
    .update({ anulado_en: new Date().toISOString(), anulado_por: yo?.mail || null })
    .eq("entrega_id", entregaId)
    .is("devuelto_en", null)
    .is("anulado_en", null)
    .select("id");
  return { cuantos: (data || []).length, error: error?.message || "" };
}

/* Quitar UN papel: se anula, y lo del catálogo que salió con él también.
 *
 * PRIMERO EL PAPEL. Si el papel no se puede anular, no se toca nada más; si
 * se anula y después fallan las herramientas, se dice cuáles quedaron —una
 * caja que muestra una llave que ya no tiene papel se arregla con «Sacar»,
 * pero sólo si alguien se entera—. */
export async function quitarPapel(entregaId) {
  if (!entregaId) return { ok: false, error: "Falta el papel." };
  const yo = actual();
  const { data, error } = await supabase
    .from("panol_entregas")
    .update({ anulado_en: new Date().toISOString(), anulado_por: yo?.mail || null })
    .eq("id", entregaId)
    .is("anulado_en", null)
    .select("id");
  if (error) return { ok: false, error: motivoEntrega(error.message) };
  if (!data || !data.length) {
    return { ok: false, error: "Ese papel ya no estaba (o no tenés permiso). Actualizá la pantalla." };
  }
  const r = await anularPrestamosDe(entregaId, yo);
  if (r.error) {
    return { ok: true, error: "",
             aviso: "El papel se quitó, pero sus herramientas del catálogo siguen en la caja: sacalas con «Sacar»." };
  }
  return { ok: true, error: "", anuladas: r.cuantos };
}

/* Quitar la caja entera según `planQuitarCaja`. Sigue aunque falle un paso y
   al final cuenta lo que no pudo: frenar a la mitad dejaría la caja a medio
   quitar sin decir qué quedó. */
export async function quitarCaja(plan) {
  const yo = actual();
  const fallas = [];
  for (const id of plan?.papeles || []) {
    const r = await quitarPapel(id);
    if (!r.ok) fallas.push(r.error);
    else if (r.aviso) fallas.push(r.aviso);
  }
  /* Lo que salió con un papel ya se anuló arriba; si alguno quedó (el papel
     ya estaba anulado de antes), se anula acá por id. */
  if ((plan?.anular || []).length) {
    const { error } = await supabase
      .from("taller_prestamo")
      .update({ anulado_en: new Date().toISOString(), anulado_por: yo?.mail || null })
      .in("id", plan.anular)
      .is("devuelto_en", null)
      .is("anulado_en", null);
    if (error) fallas.push(error.message);
  }
  for (const id of plan?.devolver || []) {
    const r = await devolver({ prestamoId: id, nota: "se quitó la caja" });
    // «Ya estaba cerrado» no es una falla: es lo que se quería.
    if (!r.ok && !/ya estaba cerrado/i.test(r.error)) fallas.push(r.error);
  }
  if (fallas.length) {
    return { ok: false, error: `No pude quitar todo: ${[...new Set(fallas)].join(" · ")}` };
  }
  return { ok: true, error: "" };
}
